import React from "react";
import { BlockMath, InlineMath } from "../../../../components/math/KatexWrappers";
import FractionInput from "../../../../components/inputs/FractionInput";
import {
  constantTerm,
  isCorrectNumber,
  leadingTerm,
  polynomialLatex,
  randInt,
  renderPreview,
  taskTags,
} from "./helpers";

function createPolynomial() {
  const degree = randInt(4, 6);
  const terms = [
    { degree, coefficient: randInt(2, 6) * (Math.random() < 0.5 ? -1 : 1) },
    { degree: degree - 2, coefficient: randInt(2, 8) * (Math.random() < 0.5 ? -1 : 1) },
    { degree: 1, coefficient: randInt(2, 9) * (Math.random() < 0.5 ? -1 : 1) },
    { degree: 0, coefficient: randInt(-12, 12) || -5 },
  ];
  const leading = leadingTerm(terms);
  return {
    terms,
    degree: leading.degree,
    leadingCoefficient: leading.coefficient,
    freeTerm: constantTerm(terms),
  };
}

export function generateTask() {
  return {
    type: "PolynomialFeaturesTask",
    points: 1,
    data: createPolynomial(),
    tags: taskTags(["stopien_wielomianu", "wspolczynniki"]),
  };
}

export function renderTask(task) {
  return (
    <>
      <p>Wyznacz stopień wielomianu, współczynnik przy najwyższej potędze oraz wyraz wolny.</p>
      <BlockMath math={`W(x)=${polynomialLatex(task.data.terms)}`} />
    </>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const answer = userAnswer || {};
  const update = (key, value) => onChange({ ...answer, [key]: value });

  return (
    <div style={{ display: "grid", gap: 12, maxWidth: 360 }}>
      <FractionInput label="\\deg W" value={answer.degree ?? ""} onChange={(value) => update("degree", value)} disabled={disabled} placeholder="np. 4" panelWidthDesktop={180} />
      <FractionInput label="a_n" value={answer.leadingCoefficient ?? ""} onChange={(value) => update("leadingCoefficient", value)} disabled={disabled} placeholder="np. -3" panelWidthDesktop={180} />
      <FractionInput label="a_0" value={answer.freeTerm ?? ""} onChange={(value) => update("freeTerm", value)} disabled={disabled} placeholder="np. 7" panelWidthDesktop={180} />
    </div>
  );
}

export function checkAnswer(task, userAnswer) {
  const checks = [
    isCorrectNumber(userAnswer?.degree, task.data.degree),
    isCorrectNumber(userAnswer?.leadingCoefficient, task.data.leadingCoefficient),
    isCorrectNumber(userAnswer?.freeTerm, task.data.freeTerm),
  ];
  const correct = checks.every(Boolean);
  return { correct, points: correct ? 1 : 0 };
}

export function renderAnswer(task) {
  return (
    <div>
      <InlineMath math={`\\deg W=${task.data.degree}`} />,{" "}
      <InlineMath math={`a_n=${task.data.leadingCoefficient}`} />,{" "}
      <InlineMath math={`a_0=${task.data.freeTerm}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  return (
    <div>
      <div>Stopień: {renderPreview(userAnswer?.degree, "\\deg W")}</div>
      <div>Współczynnik przy najwyższej potędze: {renderPreview(userAnswer?.leadingCoefficient, "a_n")}</div>
      <div>Wyraz wolny: {renderPreview(userAnswer?.freeTerm, "a_0")}</div>
    </div>
  );
}

export function renderExplanation(task) {
  const { degree, leadingCoefficient, freeTerm } = task.data;
  return (
    <div>
      <p>
        <strong>Krok 1.</strong> Najwyższa potęga zmiennej to <InlineMath math={`x^{${degree}}`} />, więc{" "}
        <InlineMath math={`\\deg W=${degree}`} />.
      </p>
      <p>
        <strong>Krok 2.</strong> Współczynnik przy tej potędze to{" "}
        <InlineMath math={`a_n=${leadingCoefficient}`} />.
      </p>
      <p>
        <strong>Krok 3.</strong> Wyraz bez zmiennej to wyraz wolny:{" "}
        <InlineMath math={`a_0=${freeTerm}`} />.
      </p>
    </div>
  );
}
